import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  Flex,
  Popover,
  SegmentedControl,
  Select,
  Text,
  Tooltip,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core';
import { Chart } from 'react-google-charts';

export type TrendDayStatus = 'ok' | 'no_spend' | 'not_synced' | 'future';

export type TrendDay = {
  date: string;
  spend: number;
  clicks: number;
  impressions: number;
  visits: number;
  conversions: number;
  revenue: number;
  status: TrendDayStatus;
};

export type TrendData = {
  eligible: boolean;
  reason: string;
  goal_name: string;
  max_range: boolean;
  range: { start: string; end: string };
  // Set when the chart starts later than the chosen range because the first campaign started then.
  campaigns_started?: string;
  campaign_count?: number;
  spend_scope?: 'report' | 'account';
  // Range ROAS already rounded to 2 decimals ("1.23"); null when the range has no spend.
  avg_roas?: string | null;
  days: TrendDay[];
  pre_days: TrendDay[];
  prev_days: TrendDay[];
  prev_range: { start: string; end: string } | null;
};

export type VelocityData = {
  eligible: boolean;
  reason: string;
  goal_name: string;
  range?: { start: string; end: string };
  campaigns_started?: string;
  campaign_count?: number;
  from?: string;
  from_label?: string;
  // Goal steps before the primary goal, in funnel order.
  from_options?: { key: string; label: string }[];
  total?: number;
  reached?: number;
  buckets?: { label: string; count: number }[];
};

export type TrendKind = 'roas' | 'rvs' | 'cpc';
export type CostMetric = 'cpc' | 'cpm';

// Hover text for the (i) icon at the start of each chart's subtitle.
export const INSIGHTS_TIPS: Record<string, React.ReactNode[]> = {
  contribution: [
    'How many converting customers each campaign reached, for the primary goal.',
    <><b>Exclusive:</b> visited only this campaign. <b>Shared:</b> also visited another selected campaign.</>,
    <><b>Potential loss:</b> conversions you could lose if this campaign were turned off.</>,
    <><b>Revenue:</b> the primary-goal revenue from customers this campaign reached. Potential loss shows the range you could lose if it were turned off.</>,
    "This chart doesn't use the Attribution setting.",
  ],
  network_contribution: [
    'How many converting customers each ad network reached, for the primary goal. Only ad visits count; Organic and Direct have no bar.',
    <><b>Exclusive:</b> this was their only paid network. <b>Shared:</b> they also came through another selected network.</>,
    <><b>Potential loss:</b> conversions you could lose if this network were cut.</>,
    <><b>Revenue:</b> the primary-goal revenue from customers this network reached. Potential loss shows the range you could lose if it were cut.</>,
    "This chart doesn't use the Attribution setting.",
  ],
  roas_trend: [
    <><b>Daily ROAS</b> = revenue ÷ ad spend for each day, using the primary goal.</>,
    <>The <b>7-day average</b> smooths out single good or bad days (daily view only). The <b>blue line</b> is the average for your selected range.</>,
    "60 days or less shows Daily and can switch to Weekly; longer ranges always show Weekly (Mon–Sun). Days with no ad spend aren't plotted. Turn on Compare to see the previous period as a dashed line.",
  ],
  rev_vs_spend: [
    <><b>Blue:</b> ad spend. <b>Green:</b> all revenue for the primary goal, the same as the summary.</>,
    'When green is taller than blue, that day or week made more than it cost.',
    "60 days or less shows Daily and can switch to Weekly; longer ranges always show Weekly (Mon–Sun). Days with no ad spend show revenue only. Turn on Compare to see the previous period as dashed lines.",
  ],
  velocity: [
    <>How long customers took to reach the <b>primary goal</b>, starting from the step you choose in <b>From</b> (the landing page or any earlier goal).</>,
    <><b>Mostly Day 0–1:</b> people decide fast, so short retargeting and follow-up are enough.</>,
    <><b>Many in Days 8–30+:</b> people take time, so longer follow-up pays off, and judging a campaign after a few days undervalues it.</>,
  ],
  cpc_trends: [
    <><b>CPC</b> = cost per click. <b>CPM</b> = cost per 1,000 impressions. Choose one for the left axis.</>,
    <><b>CR</b> = primary-goal conversions ÷ landing-page visits, always on the right axis.</>,
    "60 days or less shows Daily and can switch to Weekly; longer ranges always show Weekly (Mon–Sun). Days with no ad spend aren't plotted. Turn on Compare to see the previous period as a dashed line.",
  ],
  sources: [
    <>Where your landing-page visitors came from, by traffic source. Each visitor is counted once, under the source of their <b>first</b> visit.</>,
    <><b>Organic</b>: not from an ad, and we know the site that sent them (e.g. Google search, a Facebook post). Hover to see the sites.</>,
    <><b>Direct</b>: not from an ad, and no site was recorded (typed the address, bookmark, email or text link, QR code).</>,
    <><b>Paid</b> includes visits with a Visiopt ad tag, a click ID or a paid UTM.</>,
    <>The top 5 sources are shown; the rest are grouped as <b>Other</b>.</>,
  ],
};

// Opens on hover and on tap, below the icon.
export const InsightsInfo = ({ chart }: { chart: string }) => {
  const [opened, setOpened] = useState(false);
  const lines = INSIGHTS_TIPS[chart] ?? [];
  if (lines.length === 0) { return null; }
  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-start" width={320} withArrow shadow="md" withinPortal>
      <Popover.Target>
        <UnstyledButton
          aria-label="About this chart"
          onMouseEnter={() => setOpened(true)}
          onMouseLeave={() => setOpened(false)}
          onClick={() => setOpened((o) => !o)}
          sx={(t) => ({
            width: 18,
            height: 18,
            flex: '0 0 18px',
            borderRadius: '50%',
            border: `1px solid ${t.colorScheme === 'dark' ? t.colors.dark[4] : t.colors.gray[4]}`,
            color: t.colorScheme === 'dark' ? t.colors.dark[2] : t.colors.gray[6],
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
            fontWeight: 600,
            fontSize: 12,
            lineHeight: '16px',
            textAlign: 'center',
            cursor: 'help',
          })}
        >
          i
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown>
        {lines.map((line, i) => (
          <Text key={i} size="xs" mb={i < lines.length - 1 ? 6 : 0} sx={{ lineHeight: 1.55 }}>{line}</Text>
        ))}
      </Popover.Dropdown>
    </Popover>
  );
};

// Chart colours from the reviewed mockup, light and dark.
const PALETTE = {
  light: { cpc: '#5b8def', cpm: '#2a3f9d', cr: '#e0533f', roas: '#e0533f', cost: '#4f7ff0', rev: '#5cc896', avg: '#3b4fd8', ma: '#2e9b4f', bg: '#f8f9fa', grid: '#e8eaee', text: '#495057', strong: '#212529' },
  dark: { cpc: '#6f9bf5', cpm: '#5a6fd6', cr: '#e8604c', roas: '#e8604c', cost: '#5b8def', rev: '#4fbf8a', avg: '#7486f2', ma: '#43b866', bg: '#25262b', grid: '#34363c', text: '#a6a7ab', strong: '#e6e7ea' },
};

// Previous-period lines: the same colour blended halfway into the panel background.
const fade = (hex: string, bg: string) => {
  const mix = (i: number) => Math.round((parseInt(hex.slice(i, i + 2), 16) + parseInt(bg.slice(i, i + 2), 16)) / 2);
  return `#${[1, 3, 5].map((i) => mix(i).toString(16).padStart(2, '0')).join('')}`;
};

const parseDay = (d: string) => {
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day);
};
const longDate = (d: string) => parseDay(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const shortDate = (d: string) => parseDay(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const axisDate = (d: string) => {
  const x = parseDay(d);
  return `${String(x.getMonth() + 1).padStart(2, '0')}/${String(x.getDate()).padStart(2, '0')}/${x.getFullYear()}`;
};
const money = (v: number, digits = 2) => `$${v.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;

// Daily: one group per day. Weekly: Mon–Sun weeks, first and last may be partial.
const groupIndexes = (days: TrendDay[], weekly: boolean): number[][] => {
  if (!weekly) { return days.map((_, i) => [i]); }
  const out: number[][] = [];
  let cur: number[] = [];
  days.forEach((d, i) => {
    if (cur.length && parseDay(d.date).getDay() === 1) { out.push(cur); cur = []; }
    cur.push(i);
  });
  if (cur.length) { out.push(cur); }
  return out;
};

type Totals = { spend: number; clicks: number; impressions: number; visits: number; conversions: number; revenue: number };

// Only days with spend are plotted, so point values sum those days; `gap` explains an empty point.
const summarise = (list: TrendDay[]) => {
  const t: Totals = { spend: 0, clicks: 0, impressions: 0, visits: 0, conversions: 0, revenue: 0 };
  list.forEach((d) => {
    if (d.status !== 'ok') { return; }
    t.spend += d.spend;
    t.clicks += d.clicks;
    t.impressions += d.impressions;
    t.visits += d.visits;
    t.conversions += d.conversions;
    t.revenue += d.revenue;
  });
  const plotted = list.some((d) => d.status === 'ok');
  const gap = list.some((d) => d.status === 'not_synced' || d.status === 'future') ? 'Not synced yet' : 'No spend';
  return { t, plotted, gap };
};

const groupLabel = (list: TrendDay[], weekly: boolean) => {
  if (list.length === 0) { return ''; }
  if (!weekly) { return longDate(list[0].date); }
  return `Week of ${shortDate(list[0].date)} – ${shortDate(list[list.length - 1].date)}${list.length < 7 ? ' (partial week)' : ''}`;
};

const pctChange = (cur: number | null, prev: number | null) =>
  cur === null || prev === null || prev === 0 ? null : ((cur - prev) / prev) * 100;

// "Avg CPC $2.10 ▲ 4.0% vs $2.02"; higherIsBetter decides green or red.
// format(v, extra) adds `extra` decimals; different values that round alike get more (up to 2) so the change is visible.
const ChangeChip = ({ label, cur, prev, format, higherIsBetter }: {
  label: string; cur: number | null; prev: number | null; format: (v: number, extra: number) => string; higherIsBetter: boolean;
}) => {
  const change = pctChange(cur, prev);
  const good = change === null ? null : (change >= 0) === higherIsBetter;
  let extra = 0;
  while (cur !== null && prev !== null && cur !== prev && extra < 2 && format(cur, extra) === format(prev, extra)) { extra++; }
  return (
    <Text size="sm" component="span">
      <Text component="span" color="dimmed">{label}</Text>{' '}
      <b>{cur === null ? '–' : format(cur, extra)}</b>{' '}
      {change !== null && (
        <Text component="span" color={good ? 'teal' : 'red'} weight={600}>
          {change >= 0 ? '▲' : '▼'} {Math.abs(change).toFixed(2)}%
        </Text>
      )}{' '}
      <Text component="span" color="dimmed">vs {prev === null ? '–' : format(prev, extra)}</Text>
    </Text>
  );
};

type LegendItem = { label: string; color: string; dashed?: boolean; box?: boolean };

const Legend = ({ items }: { items: LegendItem[] }) => (
  <Flex justify="center" wrap="wrap" gap="lg" mt="sm">
    {items.map((it) => (
      <Flex key={it.label} align="center" gap={8}>
        {it.box ? (
          <Box sx={{ width: 14, height: 14, borderRadius: 2, background: it.color }} />
        ) : (
          <Box sx={{ width: 22, borderTop: `2px ${it.dashed ? 'dashed' : 'solid'} ${it.color}` }} />
        )}
        <Text size="sm">{it.label}</Text>
      </Flex>
    ))}
  </Flex>
);

type SeriesDef = { label: string; values: (number | null)[]; color: string; dashed?: boolean; axis?: 0 | 1; points?: boolean; bars?: boolean; width?: number };

// Google Charts table with one shared HTML tooltip per x position, so every series shows both periods' values.
// A line value whose neighbours are both empty draws no segment, so it gets its own dot to stay visible.
const buildTable = (labels: string[], series: SeriesDef[], tooltips: string[]) => {
  const header: any[] = ['Period'];
  series.forEach((s) => {
    header.push({ label: s.label, type: 'number' });
    header.push({ role: 'tooltip', type: 'string', p: { html: true } });
    header.push({ role: 'style', type: 'string' });
  });
  const rows = labels.map((label, i) => {
    const row: any[] = [label];
    series.forEach((s) => {
      const v = s.values[i];
      const isolated = !s.bars && v !== null && (i === 0 || s.values[i - 1] === null) && (i === s.values.length - 1 || s.values[i + 1] === null);
      row.push(v);
      row.push(tooltips[i]);
      row.push(isolated ? 'point { visible: true; size: 4 }' : null);
    });
    return row;
  });
  return [header, ...rows];
};

const tooltipHtml = (title: string, lines: string[], dark: boolean) =>
  `<div style="padding:10px 12px;font-size:12px;line-height:1.6;white-space:nowrap;background:${dark ? '#1f2024' : '#fff'};color:${dark ? '#e6e7ea' : '#212529'};border:1px solid ${dark ? '#34363c' : '#dee2e6'};border-radius:6px;box-shadow:0 4px 14px rgba(0,0,0,.15)">`
  + `<div style="font-weight:600;margin-bottom:4px">${title}</div>${lines.join('<br/>')}</div>`;

export const TrendChart = ({
  kind,
  data,
  isDarkTheme,
  compare,
  onCompareChange,
  costMetric,
  onCostMetricChange,
}: {
  kind: TrendKind;
  data: TrendData | null;
  isDarkTheme: boolean;
  compare: boolean;
  onCompareChange: (v: boolean) => void;
  costMetric: CostMetric;
  onCostMetricChange: (v: CostMetric) => void;
}) => {
  const theme = useMantineTheme();
  // Daily / Weekly picked by the user, offered only up to 60 days; longer ranges are always Weekly.
  const [grain, setGrain] = useState<'daily' | 'weekly'>('daily');
  const c = isDarkTheme ? PALETTE.dark : PALETTE.light;
  const chartKey = kind === 'roas' ? 'roas_trend' : kind === 'rvs' ? 'rev_vs_spend' : 'cpc_trends';

  if (!data || !data.eligible || data.days.length === 0) {
    return (
      <Box>
        <Flex align="center" gap={8} mb="xs">
          <InsightsInfo chart={chartKey} />
          <Text size="xs" color="dimmed">{data?.reason || 'Select campaigns to load data'}</Text>
        </Flex>
      </Box>
    );
  }

  const days = data.days;
  const n = days.length;
  const canSwitchGrain = n <= 60;
  const weekly = !canSwitchGrain || grain === 'weekly';
  const canCompare = n >= 7;
  const showPrev = compare && canCompare && data.prev_days.length === n;
  const groups = groupIndexes(days, weekly);
  const cur = groups.map((g) => g.map((i) => days[i]));
  const prev = showPrev ? groups.map((g) => g.map((i) => data.prev_days[i])) : [];
  const curSum = cur.map(summarise);
  const prevSum = prev.map(summarise);
  // Weekly points use a short "Jul 6" label so they sit straight.
  const labels = cur.map((g) => (weekly ? shortDate(g[0].date) : axisDate(g[0].date)));
  const slanted = !weekly && labels.length > 10;
  const pointSize = n <= 31 ? 5 : 0;
  const unit = weekly ? 'Weekly' : 'Daily';

  // Whole-range totals (every day, plotted or not), so the averages match the summary strip.
  const rangeTotals = (list: TrendDay[]) => list.reduce(
    (t, d) => ({ spend: t.spend + d.spend, revenue: t.revenue + d.revenue }),
    { spend: 0, revenue: 0 },
  );
  const curRange = rangeTotals(days);
  const prevRange = showPrev ? rangeTotals(data.prev_days) : null;
  const ratio = (a: number, b: number) => (b > 0 ? a / b : null);

  const series: SeriesDef[] = [];
  const legend: LegendItem[] = [];
  let summary: React.ReactNode = null;
  // A previous period with no spend and no conversions has nothing to compare against.
  const prevHasData = showPrev && data.prev_days.some((d) => d.status === 'ok' || d.revenue > 0 || d.conversions > 0);
  const noPrevData = <Text size="sm" color="dimmed" component="span">No data for the previous period</Text>;
  let vAxes: any = null;
  let vAxis: any = null;
  let extraSubtitle = '';

  // Tooltip value lines per group; "No spend" / "Not synced yet" replaces an empty value.
  const valueLines: string[][] = cur.map(() => []);
  const addLine = (i: number, label: string, v: number | null, fmt: (x: number) => string, gap: string) => {
    valueLines[i].push(`${label}: <b>${v === null ? gap : fmt(v)}</b>`);
  };

  if (kind === 'roas') {
    const roas = curSum.map((s) => (s.plotted ? ratio(s.t.revenue, s.t.spend) : null));
    // The backend rounds the range ROAS from unrounded totals exactly as the summary strip does.
    const avg = data.avg_roas !== undefined
      ? (data.avg_roas === null ? null : parseFloat(data.avg_roas))
      : ratio(curRange.revenue, curRange.spend);
    series.push({ label: `${unit} ROAS`, values: roas, color: c.roas, points: true });
    legend.push({ label: `${unit} ROAS`, color: c.roas });
    if (!weekly) {
      // 7-day window ending on each day, using the 6 days before the range so it starts on day 1.
      const all = [...data.pre_days, ...days];
      const offset = data.pre_days.length;
      // Plotted only on days with spend, so the line stops where Daily ROAS stops.
      const ma = days.map((day, i) => {
        if (day.status !== 'ok') { return null; }
        const s = summarise(all.slice(Math.max(0, offset + i - 6), offset + i + 1));
        return ratio(s.t.revenue, s.t.spend);
      });
      series.push({ label: '7-day average', values: ma, color: c.ma });
      legend.push({ label: '7-day average', color: c.ma });
    }
    series.push({ label: 'Average ROAS (selected range)', values: cur.map(() => avg), color: c.avg, width: 1.5 });
    legend.push({ label: 'Average ROAS (selected range)', color: c.avg });
    if (showPrev) {
      const pr = prevSum.map((s) => (s.plotted ? ratio(s.t.revenue, s.t.spend) : null));
      series.push({ label: `${unit} ROAS, previous period`, values: pr, color: fade(c.roas, c.bg), dashed: true });
      legend.push({ label: `${unit} ROAS, previous period`, color: fade(c.roas, c.bg), dashed: true });
      summary = prevHasData
        ? <ChangeChip label="ROAS" cur={avg} prev={prevRange ? ratio(prevRange.revenue, prevRange.spend) : null} format={(v, x) => `${v.toFixed(2 + x)}×`} higherIsBetter />
        : noPrevData;
    }
    cur.forEach((_, i) => {
      const s = curSum[i];
      addLine(i, `${unit} ROAS`, roas[i], (v) => `${v.toFixed(2)}× (${money(s.t.revenue, 0)} ÷ ${money(s.t.spend, 0)})`, s.gap);
      if (showPrev) {
        const p = prevSum[i];
        addLine(i, 'Previous period', p.plotted ? ratio(p.t.revenue, p.t.spend) : null, (v) => `${v.toFixed(2)}× (${money(p.t.revenue, 0)} ÷ ${money(p.t.spend, 0)})`, p.gap);
      }
    });
    vAxis = { minValue: 0, viewWindow: { min: 0 }, format: "#,##0.##'×'", title: 'ROAS' };
    extraSubtitle = ` • Average ROAS: ${avg === null ? '–' : `${avg.toFixed(2)}×`}`;
  } else if (kind === 'rvs') {
    // Only the cost bar waits for spend; revenue is real on any day, so it is always drawn (future days excepted).
    const revenueOf = (list: TrendDay[]) => (list.every((d) => d.status === 'future') ? null : list.reduce((t, d) => t + d.revenue, 0));
    const curRevenue = cur.map(revenueOf);
    const prevRevenue = prev.map(revenueOf);
    series.push({ label: 'Total Cost', values: curSum.map((s) => (s.plotted ? s.t.spend : null)), color: c.cost, bars: true });
    series.push({ label: 'Total Revenue', values: curRevenue, color: c.rev, bars: true });
    legend.push({ label: 'Total Cost', color: c.cost, box: true }, { label: 'Total Revenue', color: c.rev, box: true });
    if (showPrev) {
      series.push({ label: 'Cost, previous period', values: prevSum.map((s) => (s.plotted ? s.t.spend : null)), color: fade(c.cost, c.bg), dashed: true });
      series.push({ label: 'Revenue, previous period', values: prevRevenue, color: fade(c.rev, c.bg), dashed: true });
      legend.push({ label: 'Cost, previous period', color: fade(c.cost, c.bg), dashed: true }, { label: 'Revenue, previous period', color: fade(c.rev, c.bg), dashed: true });
      summary = !prevHasData ? noPrevData : (
        <>
          <ChangeChip label="Spend" cur={curRange.spend} prev={prevRange ? prevRange.spend : null} format={(v, x) => money(v, x)} higherIsBetter={false} />
          <ChangeChip label="Revenue" cur={curRange.revenue} prev={prevRange ? prevRange.revenue : null} format={(v, x) => money(v, x)} higherIsBetter />
        </>
      );
    }
    cur.forEach((_, i) => {
      const s = curSum[i];
      addLine(i, 'Total Cost', s.plotted ? s.t.spend : null, (v) => money(v), s.gap);
      addLine(i, 'Total Revenue', curRevenue[i], (v) => money(v), 'No data yet');
      if (showPrev) {
        const p = prevSum[i];
        addLine(i, 'Previous cost', p.plotted ? p.t.spend : null, (v) => money(v), p.gap);
        addLine(i, 'Previous revenue', prevRevenue[i], (v) => money(v), 'No data yet');
      }
    });
    vAxis = { minValue: 0, viewWindow: { min: 0 }, format: '$#,##0' };
  } else {
    const metricLabel = costMetric === 'cpc' ? 'CPC (Cost per Click)' : 'CPM (Cost per 1000 Impressions)';
    const short = costMetric.toUpperCase();
    const costColor = costMetric === 'cpc' ? c.cpc : c.cpm;
    // Rates come from the group's totals, never from averaging daily rates.
    const costOf = (t: Totals) => (costMetric === 'cpc' ? ratio(t.spend, t.clicks) : (t.impressions > 0 ? (t.spend / t.impressions) * 1000 : null));
    const crOf = (t: Totals) => (t.visits > 0 ? (t.conversions / t.visits) * 100 : null);
    // CR needs visits, not spend: every day's visits and conversions count, so a 0% day is plotted.
    const crOfDays = (list: TrendDay[]) => crOf(list.reduce(
      (t, d) => ({ ...t, visits: t.visits + d.visits, conversions: t.conversions + d.conversions }),
      { spend: 0, clicks: 0, impressions: 0, visits: 0, conversions: 0, revenue: 0 },
    ));
    const cost = curSum.map((s) => (s.plotted ? costOf(s.t) : null));
    const cr = cur.map(crOfDays);
    series.push({ label: metricLabel, values: cost, color: costColor, axis: 0, points: true });
    series.push({ label: 'CR (Conversion Rate)', values: cr, color: c.cr, axis: 1, points: true });
    legend.push({ label: metricLabel, color: costColor }, { label: 'CR (Conversion Rate)', color: c.cr });
    const allSum = summarise(days);
    // Avg CR covers every day in the range, so it equals the Goal Breakdown's CR for the primary goal.
    const rangeCr = (list: TrendDay[]) => {
      const t = list.reduce((acc, d) => ({ visits: acc.visits + d.visits, conversions: acc.conversions + d.conversions }), { visits: 0, conversions: 0 });
      return t.visits > 0 ? (t.conversions / t.visits) * 100 : null;
    };
    if (showPrev) {
      const prevAll = summarise(data.prev_days);
      series.push({ label: `${short}, previous period`, values: prevSum.map((s) => (s.plotted ? costOf(s.t) : null)), color: fade(costColor, c.bg), dashed: true, axis: 0 });
      series.push({ label: 'CR, previous period', values: prev.map(crOfDays), color: fade(c.cr, c.bg), dashed: true, axis: 1 });
      legend.push({ label: `${short}, previous period`, color: fade(costColor, c.bg), dashed: true }, { label: 'CR, previous period', color: fade(c.cr, c.bg), dashed: true });
      summary = !prevHasData ? noPrevData : (
        <>
          <ChangeChip label={`Avg ${short}`} cur={costOf(allSum.t)} prev={costOf(prevAll.t)} format={(v, x) => money(v, 2 + x)} higherIsBetter={false} />
          <ChangeChip label="Avg CR" cur={rangeCr(days)} prev={rangeCr(data.prev_days)} format={(v, x) => `${v.toFixed(2 + x)}%`} higherIsBetter />
        </>
      );
    }
    cur.forEach((_, i) => {
      const s = curSum[i];
      addLine(i, short, cost[i], (v) => money(v), s.gap);
      addLine(i, 'CR', cr[i], (v) => `${v.toFixed(2)}%`, 'No visits');
      if (showPrev) {
        const p = prevSum[i];
        addLine(i, `Previous ${short}`, p.plotted ? costOf(p.t) : null, (v) => money(v), p.gap);
        addLine(i, 'Previous CR', crOfDays(prev[i]), (v) => `${v.toFixed(2)}%`, 'No visits');
      }
    });
    const axisTitle = { color: c.text, italic: false };
    vAxes = {
      0: { title: `${short} ($)`, format: '$#,##0.##', minValue: 0, viewWindow: { min: 0 }, textStyle: { color: c.text }, titleTextStyle: axisTitle, gridlines: { color: c.grid }, baselineColor: c.grid },
      1: { title: 'Conversion Rate (%)', format: "#,##0.##'%'", minValue: 0, viewWindow: { min: 0 }, textStyle: { color: c.text }, titleTextStyle: axisTitle, gridlines: { color: 'transparent' }, baselineColor: 'transparent' },
    };
  }

  const tooltips = cur.map((g, i) => {
    const title = showPrev ? `${groupLabel(g, weekly)} vs ${groupLabel(prev[i], weekly).replace('Week of ', '')}` : groupLabel(g, weekly);
    return tooltipHtml(title, valueLines[i], isDarkTheme);
  });
  const table = buildTable(labels, series, tooltips);

  const seriesOptions: Record<number, any> = {};
  series.forEach((s, i) => {
    seriesOptions[i] = {
      color: s.color,
      lineWidth: s.width ?? 2,
      pointSize: s.points ? pointSize : 0,
      ...(s.dashed ? { lineDashStyle: [6, 5] } : {}),
      ...(s.axis !== undefined ? { targetAxisIndex: s.axis } : {}),
      ...(kind === 'rvs' && !s.bars ? { type: 'line' } : {}),
    };
  });

  const options: any = {
    backgroundColor: 'transparent',
    legend: 'none',
    tooltip: { isHtml: true },
    interpolateNulls: false,
    // Daily: up to 45 points every date is labelled, longer ranges keep about 30; past 10 points the labels tilt.
    // Weekly: straight short labels, every other one past 20 weeks so they never overlap.
    chartArea: { top: 16, bottom: slanted ? 80 : 40, left: 90, right: kind === 'cpc' ? 70 : 24, width: '100%', height: '100%' },
    hAxis: {
      textStyle: { color: c.text, fontSize: 11 },
      showTextEvery: weekly ? (labels.length > 20 ? 2 : 1) : (labels.length <= 45 ? 1 : Math.ceil(labels.length / 30)),
      slantedText: slanted,
      slantedTextAngle: 45,
      maxAlternation: 1,
    },
    series: seriesOptions,
    ...(kind === 'rvs' ? { seriesType: 'bars', bar: { groupWidth: '70%' } } : {}),
    ...(vAxes ? { vAxes } : { vAxis: { ...vAxis, textStyle: { color: c.text }, titleTextStyle: { color: c.text, italic: false }, gridlines: { color: c.grid }, baselineColor: c.grid } }),
  };

  const startedNote = data.campaigns_started
    ? ` (${(data.campaign_count ?? 1) > 1 ? 'campaigns' : 'campaign'} started ${longDate(data.campaigns_started)})`
    : '';
  const rangeText = `${longDate(data.range.start)} – ${longDate(data.range.end)}${startedNote}`;
  const subtitleHead = `Showing ${rangeText} • Goal: ${data.goal_name} • ${weekly ? 'Weekly (Mon–Sun)' : 'Daily'} • `;
  const spendTip = (
    <Tooltip
      multiline
      width={300}
      withArrow
      withinPortal
      events={{ hover: true, focus: true, touch: true }}
      label={data.spend_scope === 'account'
        ? 'Full daily spend for everything in the connected ad account, including campaigns that send no visits.'
        : 'Ad spend from the campaigns in this report.'}
    >
      <Text component="span" tabIndex={0} sx={{ borderBottom: '1px dotted currentColor', cursor: 'help' }}>
        {data.spend_scope === 'account' ? 'Spend: full ad account' : 'Spend: campaigns in this report'}
      </Text>
    </Tooltip>
  );

  return (
    <Box>
      {/* The caption wraps in its own column so a longer caption never pushes the controls onto a new row. */}
      <Flex align="center" justify="space-between" gap="md" wrap="wrap">
        <Flex align="center" gap={8} sx={{ flex: '1 1 320px', minWidth: 0 }}>
          <InsightsInfo chart={chartKey} />
          <Text size="xs" color="dimmed">
            {subtitleHead}{spendTip}{kind === 'rvs' ? '' : extraSubtitle}{data.max_range ? ' (max range exceeds 180 days)' : ''}
          </Text>
        </Flex>
        <Flex align="center" gap="md" sx={{ flex: '0 0 auto', marginLeft: 'auto' }}>
          {canSwitchGrain && (
            <SegmentedControl
              value={weekly ? 'weekly' : 'daily'}
              onChange={(v) => setGrain(v as 'daily' | 'weekly')}
              color="teal"
              size="xs"
              data={[{ label: 'Daily', value: 'daily' }, { label: 'Weekly', value: 'weekly' }]}
            />
          )}
          {canCompare && (
            <Checkbox
              size="xs"
              color="teal"
              label="Compare to previous period"
              checked={compare}
              onChange={(e) => onCompareChange(e.currentTarget.checked)}
            />
          )}
          {kind === 'cpc' && (
            <Flex align="center" gap="xs">
              <Text size="xs" color="dimmed">Cost metric</Text>
              <SegmentedControl
                value={costMetric}
                onChange={(v) => onCostMetricChange(v as CostMetric)}
                color="teal"
                size="xs"
                data={[{ label: 'CPC', value: 'cpc' }, { label: 'CPM', value: 'cpm' }]}
              />
            </Flex>
          )}
        </Flex>
      </Flex>
      {showPrev && data.prev_range && (
        <Text size="xs" color="dimmed" mt={4}>
          Compared with {longDate(data.prev_range.start)} – {longDate(data.prev_range.end)} (previous {n} days)
        </Text>
      )}
      {summary && (
        <Flex gap="xl" wrap="wrap" mt="sm">{summary}</Flex>
      )}
      <Box
        mt="xs"
        sx={{
          // Google wraps HTML tooltips in its own white box; the tooltip markup carries the themed look instead.
          '& .google-visualization-tooltip': { background: 'transparent', border: 'none', boxShadow: 'none', padding: 0 },
          color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.black,
        }}
      >
        <Chart
          key={`${kind}-${costMetric}-${showPrev ? 1 : 0}-${isDarkTheme ? 1 : 0}-${weekly ? 'w' : 'd'}`}
          chartType={kind === 'rvs' ? 'ComboChart' : 'LineChart'}
          data={table}
          options={options}
          width="100%"
          height={labels.length > 10 ? '360px' : '320px'}
        />
      </Box>
      <Legend items={legend} />
    </Box>
  );
};

export const VelocityChart = ({
  data,
  isDarkTheme,
  onFromChange,
}: {
  data: VelocityData | null;
  isDarkTheme: boolean;
  onFromChange: (v: string) => void;
}) => {
  const c = isDarkTheme ? PALETTE.dark : PALETTE.light;

  if (!data || !data.eligible || !data.buckets) {
    return (
      <Flex align="center" gap={8} mb="xs">
        <InsightsInfo chart="velocity" />
        <Text size="xs" color="dimmed">{data?.reason || 'Select campaigns to load data'}</Text>
      </Flex>
    );
  }

  const buckets = data.buckets;
  const reached = data.reached ?? 0;
  const total = data.total ?? 0;
  const fromLabel = data.from_label ?? 'Landing page (first click)';
  const fromKey = data.from ?? 'lp';
  const toLabel = data.goal_name;
  const pct = (v: number) => (reached > 0 ? ((v / reached) * 100).toFixed(2) : '0.00');
  const convWord = (n: number) => (n === 1 ? 'conversion' : 'conversions');
  // About 20% headroom above the tallest bar (at least one whole count) so every label sits above its bar.
  const maxCount = Math.max(0, ...buckets.map((b) => b.count));
  const axisMax = Math.max(maxCount + 1, Math.ceil(maxCount * 1.2));
  // Nothing to plot: show "No data" instead of an empty chart with a meaningless axis.
  const hasData = maxCount > 0;
  const countLine = reached === 0
    ? `No customers reached ${toLabel} after ${fromLabel} in this date range.`
    : fromKey === 'lp'
      ? `${total} ${convWord(total)}`
      : `${reached} of ${total} ${convWord(total)} passed through ${fromLabel} first`;

  const table = [
    ['Bucket', 'Conversions', { role: 'style' }, { role: 'annotation' }, { role: 'tooltip', type: 'string', p: { html: true } }],
    ...buckets.map((b) => [
      b.label,
      b.count,
      `color: ${c.cost}`,
      b.count > 0 ? `${b.count} (${pct(b.count)}%)` : '0',
      tooltipHtml(b.label, [`<b>${b.count}</b> ${convWord(b.count)} (${pct(b.count)}%)`], isDarkTheme),
    ]),
  ];
  const options: any = {
    backgroundColor: 'transparent',
    legend: 'none',
    tooltip: { isHtml: true },
    chartArea: { top: 28, bottom: 40, left: 50, right: 16, width: '100%', height: '100%' },
    bar: { groupWidth: '60%' },
    annotations: { alwaysOutside: true, textStyle: { color: c.strong, fontSize: 12, bold: true }, stem: { length: 4, color: 'transparent' } },
    hAxis: { textStyle: { color: c.text } },
    vAxis: { minValue: 0, viewWindow: { min: 0, max: axisMax }, format: '0', textStyle: { color: c.text }, gridlines: { color: c.grid }, baselineColor: c.grid },
  };
  const startedNote = data.campaigns_started
    ? ` (${(data.campaign_count ?? 1) > 1 ? 'campaigns' : 'campaign'} started ${longDate(data.campaigns_started)})`
    : '';
  const range = data.range ? `${longDate(data.range.start)} – ${longDate(data.range.end)}${startedNote}` : '';

  return (
    <Box>
      <Flex align="center" gap={8}>
        <InsightsInfo chart="velocity" />
        <Text size="xs" color="dimmed">
          <Text component="span" weight={600} color={isDarkTheme ? 'gray.3' : 'dark.6'}>{fromLabel} → {toLabel}</Text>
          {range ? ` • Showing ${range}` : ''} •{' '}
          <Tooltip label="Only customers who came from a paid ad (excludes organic and direct)." withArrow withinPortal multiline width={260}>
            <Text component="span" sx={{ borderBottom: '1px dotted', cursor: 'help' }}>Paid Only</Text>
          </Tooltip>
        </Text>
      </Flex>
      <Flex align="center" gap="sm" mt="sm" wrap="wrap">
        <Text size="sm" color="dimmed">From:</Text>
        <Select
          size="xs"
          // The step the backend used; a remembered step missing from these campaigns falls back to the landing page.
          value={fromKey}
          onChange={(v) => onFromChange(v || 'lp')}
          data={[
            { value: 'lp', label: 'Landing page (first click)' },
            ...(data.from_options ?? []).map((o) => ({ value: o.key, label: o.label })),
          ]}
          withinPortal
          sx={{ minWidth: 240 }}
        />
        <Text size="sm" color="dimmed">→ To:</Text>
        {/* Fixed to the report's primary goal; changed through the Primary Goal filter, not here. */}
        <Box
          title="Set by the report's Primary Goal"
          sx={(t) => ({ padding: '4px 10px', border: `1px dashed ${t.colorScheme === 'dark' ? t.colors.dark[4] : t.colors.gray[4]}`, borderRadius: 6 })}
        >
          <Text size="sm" weight={600} component="span">{toLabel}</Text>{' '}
          <Text size="sm" color="dimmed" component="span">(primary goal)</Text>
        </Box>
      </Flex>
      <Text size="xs" color="dimmed" mt="sm">{countLine}</Text>
      {hasData ? (
        <Chart chartType="ColumnChart" data={table} options={options} width="100%" height="320px" />
      ) : (
        <Flex align="center" justify="center" h={320}>
          <Text size="sm" color="dimmed">No data</Text>
        </Flex>
      )}
    </Box>
  );
};
